import {
  DEFAULT_RANGE_PRESET,
  FINE_RESOLUTION_MAX_MINUTES,
  MAX_RANGE_MINUTES,
  RANGE_PRESETS,
  RESOLUTION_BUCKET_MS,
  type RangePreset,
} from '@/features/telemetry/config';
import type { TelemetryResolution } from '@/features/telemetry/services/schemas';

/**
 * The route's search params, shaped independently of zod so this module stays a pure
 * function with no schema dependency. The route's `validateSearch` schema produces a
 * structurally compatible shape.
 */
export interface OverviewRangeSearch {
  preset?: RangePreset;
  /** ISO-8601 datetime, present only for a custom range. */
  from?: string;
  /** ISO-8601 datetime, present only for a custom range. */
  to?: string;
}

export interface ResolvedRange {
  from: Date;
  to: Date;
  resolution: TelemetryResolution;
  preset: RangePreset | 'custom';
}

const MINUTE_MS = 60_000;

function minutesToMs(minutes: number): number {
  return minutes * MINUTE_MS;
}

function floorToBucket(date: Date, bucketMs: number): Date {
  return new Date(Math.floor(date.getTime() / bucketMs) * bucketMs);
}

function resolutionForSpan(spanMs: number): TelemetryResolution {
  return spanMs <= minutesToMs(FINE_RESOLUTION_MAX_MINUTES) ? '1min' : '15min';
}

/**
 * Resolves the page's search params into a concrete, clamped, bucket-aligned range.
 *
 * Rules, applied in order:
 * 1. `from`/`to` present -> a custom range; otherwise derive from `preset`
 *    (defaulting to `DEFAULT_RANGE_PRESET`) relative to `now`.
 * 2. Clamp `to` to `now` — never request future data.
 * 3. Cap the span at `MAX_RANGE_MINUTES`, trimming from the start (`from` moves, `to`
 *    stays put) — the API itself allows up to 31 days, this is a UI-level choice.
 * 4. An inverted or zero-width range (bad/stale `from`/`to`) falls back to the default
 *    preset entirely, rather than trying to repair it.
 * 5. Resolution is chosen from the (possibly capped) span.
 * 6. Both bounds are floored to that resolution's bucket boundary. This keeps the
 *    TanStack Query key stable across re-renders within the same bucket, at the cost of
 *    `to` trailing real time by up to one bucket — acceptable, since a partial trailing
 *    bucket has no completed sample to plot anyway.
 */
export function resolveRange(search: OverviewRangeSearch, now: Date): ResolvedRange {
  const isCustom = search.from !== undefined && search.to !== undefined;

  let from: Date;
  let to: Date;
  let preset: RangePreset | 'custom';

  if (isCustom) {
    from = new Date(search.from!);
    to = new Date(search.to!);
    preset = 'custom';
  } else {
    const activePreset = search.preset ?? DEFAULT_RANGE_PRESET;
    to = now;
    from = new Date(now.getTime() - minutesToMs(RANGE_PRESETS[activePreset].minutes));
    preset = activePreset;
  }

  // Never request future data.
  if (to.getTime() > now.getTime()) {
    to = now;
  }

  // Cap the span, trimming from the start.
  if (to.getTime() - from.getTime() > minutesToMs(MAX_RANGE_MINUTES)) {
    from = new Date(to.getTime() - minutesToMs(MAX_RANGE_MINUTES));
  }

  // Inverted or zero-width — the input can't be salvaged, fall back entirely.
  if (to.getTime() <= from.getTime()) {
    return resolveRange({ preset: DEFAULT_RANGE_PRESET }, now);
  }

  const resolution = resolutionForSpan(to.getTime() - from.getTime());
  const bucketMs = RESOLUTION_BUCKET_MS[resolution];

  return {
    from: floorToBucket(from, bucketMs),
    to: floorToBucket(to, bucketMs),
    resolution,
    preset,
  };
}
